import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import i18next from 'i18next';

import { NavLink, useRouteMatch } from 'react-router-dom';
import Icon from '../../../../components/icon/Icon';
import Image from '../../../../components/image/Image';
import { BASE_URL } from '../../../../core/constants/api';
import { RootState } from '../../../../core/state/state';
import defaultUserImage from '../../../../../img/user-default.png';
import { Languages } from '../../../../core/enums/Languages';
import AccountService from '../../../../core/services/AccountService';
import { getUserInfo } from '../../../../core/state/user/asyncActions';
import InteractiveElement from '../../../../components/interactive-element/InteractiveElement';
import ConfirmModal from '../../confirm-modal/ConfirmModal';
import { logout, logoutExternal } from '../../../../core/state/auth/asyncActions';
import { HOME_ROUTE, PROJECT_BOOKINGS } from '../../../../core/constants/routes';

import './HeaderMobileMenu.scss';

interface Props {
  closeMenu: () => void;
}

const HeaderMobileMenu: React.FC<Props> = ({ closeMenu }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isProjectsPage = useRouteMatch({ path: HOME_ROUTE, exact: true });
  const isBookingPage = useRouteMatch(PROJECT_BOOKINGS);

  const [language, setLanguage] = useState<string | null>(Languages.De);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);

  const user = useSelector((state: RootState) => state.user.userInfo);
  const projectId = useSelector((state: RootState) => state.user.userInfo?.projectID);
  const account = useSelector((state: RootState) => state.auth.authInfo?.account);

  const changeLanguage = async (lng: Languages) => {
    setLanguage(lng);
    i18next.changeLanguage(lng);
    localStorage.setItem('i18nextLng', lng);

    await AccountService.changeLanguage(lng);
    dispatch(getUserInfo());
  };

  useEffect(() => {
    const lng = localStorage.getItem('i18nextLng');
    if (lng) {
      i18next.changeLanguage(lng);
      setLanguage(lng);
    }
  }, []);

  return (
    <div className="header-mobile-menu">
      <div>
        <NavLink
          className={`sidebar__link ${isProjectsPage || isBookingPage ? 'sidebar__link--active' : ''}`}
          onClick={closeMenu}
          to={account.environment ? HOME_ROUTE : `/projects/${projectId}/bookings`}
        >
          <Icon className="header-mobile-menu__link-icon" icon="projectsDark" size="full-size" svg />
          <span className="header-mobile-menu__link-text">{account.environment ? t('sidebar.link.allProjects') : t('sidebar.link.myProject')}</span>
        </NavLink>
        <InteractiveElement className="sidebar__link" onClick={() => setIsLogoutModalOpen(true)}>
          <Icon className="header-mobile-menu__link-icon" icon="logout" svg size="full-size" />
          <span className="header-mobile-menu__link-text">{t('header.dropdown.logout')}</span>
        </InteractiveElement>
        <InteractiveElement
          className={`header-mobile-menu__language ${language === Languages.En ? 'header-mobile-menu__language--active' : ''}`}
          onClick={() => changeLanguage(Languages.En)}
        >
          {t('common.languageEn')}
        </InteractiveElement>
        <InteractiveElement
          className={`header-mobile-menu__language ${language === Languages.De ? 'header-mobile-menu__language--active' : ''}`}
          onClick={() => changeLanguage(Languages.De)}
        >
          {t('common.languageDe')}
        </InteractiveElement>
        <div className="d-flex pt-4">
          <Image
            className="header-mobile-menu__user-img"
            src={`${BASE_URL}/${user?.profilePhoto}`}
            alt="user avatar"
            onError={(e: SyntheticEvent<HTMLImageElement, Event>) => {
              e.currentTarget.src = defaultUserImage;
            }}
          />
          <div className="d-flex flex-column">
            <span className="header-mobile-menu__user-name text-truncate">{account?.name}</span>
            <span className="header-mobile-menu__user-email text-truncate">{account?.username}</span>
          </div>
        </div>
      </div>

      <ConfirmModal
        size="extra-small"
        titleText={t('modal.logout.title')}
        contentText={t('modal.logout.text')}
        isOpen={isLogoutModalOpen}
        onCancel={() => setIsLogoutModalOpen(false)}
        onConfirm={() => (account.environment ? dispatch(logout()) : dispatch(logoutExternal()))}
      />
    </div>
  );
};

export default HeaderMobileMenu;
